import { getHttpUrl, getWebSocketUrl } from "./serverUrls";
import {
  getActiveConversationSession,
  createSession,
  startSession,
  endSession,
  SessionSpec,
  SessionStartResponse,
  SessionStatus,
  StartSessionFailureClass,
  APISession,
} from "./sessionEndpoints";
import { api } from "../api_middleware";
import { getConversationInfoFromToken } from "./jwtUtils";
import { getSignallingServerURL } from "./signallingServers";
import { makeErrorClass } from "../utils/customErrors";

export type SessionSetupResult = {
  conversationId: string;
  baseUrl: string;
  websocketUrl: string;
  pixelStreamerURL?: string;
  sessionId?: string;
  streamToken?: string;
  apiSession: APISession;
};

export type TokenSetupResult = {
  conversationId: string;
  baseUrl: string;
  websocketUrl: string;
  pixelStreamerURL?: string;
  userIsHost: boolean;
  tokenMicEnabled: boolean;
  userId?: string;
  ssOverrideVal?: string;
};

export const SessionStartCancelledError = makeErrorClass("SessionStartCancelledError");
export const SessionStartTimeoutError = makeErrorClass("SessionStartTimeoutError");

/* ----------------‑ helpers ---------------- */

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

function getPixelStreamerEndpointUrl(
  spec: SessionSpec,
  sessionId: string,
  streamToken: string,
  jwt?: string,
): string {
  const base = getHttpUrl(
    spec.pixelsConfig.environment,
    spec.pixelsConfig.localDevConnectionMethod,
    spec.pixelsConfig.port,
    spec.preventEnvOverride
  );
  const url = `${base}v1/pixels/sessions/connect-to-stream/${sessionId}` +
    `?stream_token=${streamToken}${jwt ? `&token=${jwt}` : ""}`;
  return url;
}

function checkAbort(abortSignal?: AbortSignal) {
  if (abortSignal?.aborted) {
    const err = new Error("Operation was aborted");
    err.name = "AbortError";
    throw err;
  }
}

/**
 * Calls startSession on the session until it:
 * - succeeds and returns a stream token: returns SessionStartResponse
 * - fails with a session cancelled error: throws SessionStartCancelledError
 * - times out after maxTries: throws SessionStartTimeoutError
 */
async function ensureSessionStarted(
  spec: SessionSpec,
  sessionId: string,
  maxTries: number = 15,
  abortSignal?: AbortSignal
): Promise<SessionStartResponse> {
  console.log("ensureSessionStarted", sessionId, spec);

  // Want to test the session retry UI? Uncomment these lines:
  // await sleep(2_000);
  // throw new SessionStartTimeoutError("Session start timed out.");

  let v = maxTries;
  while (v > 0) {
    // Check if cancelled before each attempt
    checkAbort(abortSignal);

    try {
      const resp = await startSession(spec, sessionId);
      if (resp.failure == null && resp.stream_token !== null) {
        return resp;
      } else if (resp.failure && resp.failure.failure_class === StartSessionFailureClass.SessionCancelled) {
        console.error("ensureSessionStarted: session was cancelled, returning undefined");
        throw new SessionStartCancelledError("Session was cancelled.");
      } else {
        // Check if cancelled before sleeping
        if (abortSignal?.aborted) {
          const error = new Error("Operation was aborted");
          error.name = "AbortError";
          throw error;
        }
        await sleep(2_000);
      }
      v -= 1;
    } catch (err: any) {
      if (err.status === 409) {
        console.error("ensureSessionStarted: session already exists (got 409), continuing...");
        // If we got a 409, it means the session is already in progress.
        // We can just continue and try to start it again.
        //continue;
        v -= 1;
        // Check if cancelled before sleeping
        if (abortSignal?.aborted) {
          const error = new Error("Operation was aborted");
          error.name = "AbortError";
          throw error;
        }
        await sleep(2_000);
      } else {
        throw err; // re-throw any other error
      }
    }
  }
  console.log("ensureSessionStarted timed out after", maxTries, "tries");
  throw new SessionStartTimeoutError("Session start timed out.");
}

async function createOrReuseSession(spec: SessionSpec, maxTries: number = 3, abortSignal?: AbortSignal): Promise<APISession> {

  // Try to create a session; if we get back an existing session with a different res_link_params_id, 
  // kill it and retry. Otherwise, return the newly-created session or re-use the existing one.
  // To protect against server bugs, limit retries.

  for (let tries = 0; tries < maxTries; tries++) {
    checkAbort(abortSignal);

    try {
      const createSessionResponse = await createSession(spec);

      if (createSessionResponse.session) {
        // We got a session (no errors), so we're done.
        console.log("[createOrReuseSession] created session:", createSessionResponse.session);
        return createSessionResponse.session;
      }

      const err = createSessionResponse.error;
      if (!err) {
        // No session, no error: surely a programming bug. Exit immediately.
        console.error("[createOrReuseSession] no session, no error - wat?");
        throw new Error("Failed to create session.");
      }

      if (err.link_consumption_exhausted) {
        // First error type: the user cannot go any more. Just tell them I guess...
        throw new Error(err.link_consumption_exhausted.error_text || "Link consumption exhausted.");
      }

      const existingSession = err.existing_active_session?.session;
      if (existingSession) {
        // Second error type: there's already a running session.
        const existingResLink = existingSession.pixel_streaming_experience?.res_link_params_id;
        if (existingResLink) {
          console.log("[createOrReuseSession] found existing experience res link: ", existingResLink);
          if (existingResLink === spec.resLinkParamsId) {
            console.log("[createOrReuseSession] found existing session with same res_link_params_id, using it.");
            return existingSession;
          }

          console.log("[createOrReuseSession] found existing session with different res_link_params_id, killing it, going round...");
          checkAbort(abortSignal);
          await endSession(spec, existingSession.session_id);

          // The real reason for the outer for loop: just go again. But limit attempts.
          continue;
        }

        // This can happen in manual session mode I suppose...
        console.log("[createOrReuseSession] found existing session without pixel streaming experience, using it.");
        return existingSession;
      }

      console.error("[createOrReuseSession] error, non-specific, failed to create.");
      throw new Error("Failed to create session.");
    } catch (err: any) {
      console.error("[createOrReuseSession] got true exception on createSession:", err);
      throw err;
    }
  }

  console.error("[createOrReuseSession] unable to create a new session after multiple attempts.");
  throw new Error("Failed to create session.");
}

/* ─────────────  public flows  ───────────── */

export type SessionCreatedCallback = (spec: SessionSpec, sessionId: string) => void;

async function ensureSessionCreatedAndStarted(
  spec: SessionSpec,
  maxTries: number = 3,
  sessionCreatedCallback: SessionCreatedCallback | undefined = undefined,
  abortSignal?: AbortSignal
): Promise<SessionStartResponse> {
  console.log("ensureSessionCreatedAndStarted", spec);

  for (let tries = 0; tries < maxTries; tries++) {
    checkAbort(abortSignal);

    // First, see if there's an existing session for our conversation id.
    let activeSession = await getActiveConversationSession(spec);

    if (activeSession && activeSession.status === SessionStatus.InProgress) {
      // start it again anyway, so we get a new token...
      return await startSession(spec, activeSession.session_id);
    }

    // Create anew or re-use an existing session. Can throw its own exception.
    activeSession = await createOrReuseSession(spec, 3, abortSignal);

    if (sessionCreatedCallback) {
      sessionCreatedCallback(spec, activeSession.session_id);
    }

    // Ensure it starts. This can throw several exceptions...
    try {
      return await ensureSessionStarted(spec, activeSession.session_id, 15, abortSignal);
    } catch (err: any) {
      if (err instanceof SessionStartCancelledError) {
        console.error("ensureSessionCreatedAndStarted: session start was cancelled; will try again...");
        // Will simply continue the loop...
      } else if (err instanceof SessionStartTimeoutError) {
        console.error("ensureSessionCreatedAndStarted: session start timed out.");
        throw err;
      } else if (err instanceof Error && err.name === "AbortError") {
        console.error("ensureSessionCreatedAndStarted: session start was aborted.");
        throw err;
      } else {
        console.error("ensureSessionCreatedAndStarted: unknown error during session start:", err);
        throw new Error("Unknown error during session start: " + err.message);
      }
    }
  }

  console.error("ensureSessionCreatedAndStarted: max tries reached, throwing error.");
  throw new SessionStartCancelledError("Session was cancelled max num times; failing for good.");
}

async function directPixelStreamingFlow(spec: SessionSpec, jwt?: string, sessionCreatedCallback?: SessionCreatedCallback, abortSignal?: AbortSignal): Promise<SessionSetupResult> {
  console.log("directPixelStreamingFlow", spec);

  const startResponse = await ensureSessionCreatedAndStarted(spec, 3, sessionCreatedCallback, abortSignal);

  if (startResponse.stream_token === null) {
    throw new Error("Session creation error.");
  }

  console.log("directPixelStreamingFlow response:", startResponse);

  const streamerUrl = getPixelStreamerEndpointUrl(spec, startResponse.session.session_id, startResponse.stream_token, jwt);

  console.log("directPixelStreamingFlow streamerUrl:", streamerUrl);

  return {
    conversationId: startResponse.session.conversation_id,
    baseUrl: getHttpUrl(spec.pixelsConfig.environment, spec.pixelsConfig.localDevConnectionMethod, undefined, spec.preventEnvOverride),
    websocketUrl: getWebSocketUrl(startResponse.session.conversation_id, spec.puppetConfig.environment, spec.puppetConfig.localDevConnectionMethod, spec.preventEnvOverride),
    pixelStreamerURL: streamerUrl,
    sessionId: startResponse.session.session_id,
    streamToken: startResponse.stream_token,
    apiSession: startResponse.session as APISession,
  };
}

/**
 * The user starts the conversation via the token flow that
 * we get from the old ops join token.
 */
async function tokenFlow(joinToken: string): Promise<TokenSetupResult> {
  const info = getConversationInfoFromToken(joinToken);
  if (!info) {
    throw new Error("Invalid join token");
  }

  const baseUrl = getHttpUrl(info.environment, info.local_dev_connection_method);

  const connectResult = await api("v2/puppet/conversation-connect", "POST", baseUrl, {
    conversation_id: info.conversation_id,
    join_token: joinToken,
  });

  if (
    !connectResult.conversation_status.brain_alive ||
    !connectResult.conversation_status.pixel_streaming_client_connected
  ) {
    throw new Error("SERVICE_UNAVAILABLE");
  }

  return {
    conversationId: info.conversation_id,
    baseUrl: baseUrl,
    websocketUrl: getWebSocketUrl(info.conversation_id, info.environment, info.local_dev_connection_method)!,
    pixelStreamerURL: getSignallingServerURL(info.ss_override),
    userIsHost: connectResult.conversation_status.user_is_host,
    tokenMicEnabled: info.mic_enabled ?? false,
    userId: info.user_id,
    ssOverrideVal: info.ss_override ?? undefined,
  };
}

export const SessionSetupFlows = {
  directPixelStreamingFlow,
  tokenFlow,
};
